import { useState, useEffect } from 'preact/hooks'
import { useTranslation } from 'react-i18next'
import { Dialog } from './Dialog'
import { Button } from './Button'
import { Alert } from './Alert'
import { FileSelector } from './FileSelector'
import { RadioGroup } from './RadioGroup'
import { Input } from './Input'
import { Select } from './Select'
import { Checkbox } from './Checkbox'
import {
  type DocumentSignatureFormat,
  SIGNATURE_TYPES,
} from '@/utils/signatureTypes'

export interface Step1Data {
  file: File
  signatureType: 'freeSignature' | 'paidSignature'
  signatureCode: string
  signatureFormat: DocumentSignatureFormat
  enableGraphicSignature: boolean
}

interface FormState {
  selectedFile: File | null
  signatureType: string
  signatureCode: string
  signatureFormat: DocumentSignatureFormat
  enableGraphicSignature: boolean
  error: string | null
}

interface AddDocumentDialogStep1Props {
  isOpen: boolean
  onClose: () => void
  isFreeSignatureEnabled: boolean
  graphicSignatureEnabled: boolean
  validateFile: (file: File) => string | null
  onContinue: (data: Step1Data) => void
  // Optional initial data for form persistence
  initialData?: Partial<Step1Data> | null
  inputCodeError?: string | null | undefined
  isLoading?: boolean
  error?: string | null
  onErrorDismiss?: () => void
  onInputCodeChange?: () => void
}

export function AddDocumentDialogStep1({
  isOpen,
  onClose,
  isFreeSignatureEnabled,
  graphicSignatureEnabled,
  validateFile,
  onContinue,
  initialData = null,
  inputCodeError = null,
  isLoading = false,
  error: externalError = null,
  onErrorDismiss,
  onInputCodeChange,
}: AddDocumentDialogStep1Props) {
  const { t } = useTranslation()
  const [formState, setFormState] = useState<FormState>(() => ({
    selectedFile: initialData?.file ?? null,
    signatureType:
      initialData?.signatureType ??
      (isFreeSignatureEnabled ? 'freeSignature' : 'paidSignature'),
    signatureCode: initialData?.signatureCode ?? '',
    signatureFormat: initialData?.signatureFormat ?? 'PAdES',
    enableGraphicSignature: initialData?.enableGraphicSignature ?? true,
    error: null,
  }))

  useEffect(() => {
    if (isOpen) {
      setFormState({
        selectedFile: initialData?.file ?? null,
        signatureType:
          initialData?.signatureType ??
          (isFreeSignatureEnabled ? 'freeSignature' : 'paidSignature'),
        signatureCode: initialData?.signatureCode ?? '',
        signatureFormat: initialData?.signatureFormat ?? 'PAdES',
        enableGraphicSignature: initialData?.enableGraphicSignature ?? true,
        error: null,
      })
    }
  }, [isOpen, isFreeSignatureEnabled, initialData])

  useEffect(() => {
    if (
      !isFreeSignatureEnabled &&
      formState.signatureType === 'freeSignature'
    ) {
      setFormState(prev => ({ ...prev, signatureType: 'paidSignature' }))
    }
  }, [isFreeSignatureEnabled, formState.signatureType])

  useEffect(() => {
    const isPAdESFormat =
      formState.signatureType === 'freeSignature' ||
      formState.signatureFormat === 'PAdES'
    if (
      (!isPAdESFormat || !graphicSignatureEnabled) &&
      formState.enableGraphicSignature
    ) {
      setFormState(prev => ({ ...prev, enableGraphicSignature: false }))
    }
  }, [
    formState.signatureType,
    formState.signatureFormat,
    formState.enableGraphicSignature,
    graphicSignatureEnabled,
  ])

  const handleContinue = () => {
    if (!formState.selectedFile) {
      setFormState(prev => ({
        ...prev,
        error: t('documentAdd.errors.uploadFailed'),
      }))
      return
    }

    const validationError = validateFile(formState.selectedFile)
    if (validationError) {
      setFormState(prev => ({ ...prev, error: validationError }))
      return
    }

    onContinue({
      file: formState.selectedFile,
      signatureType: formState.signatureType as
        | 'freeSignature'
        | 'paidSignature',
      signatureCode: formState.signatureCode,
      signatureFormat: formState.signatureFormat,
      enableGraphicSignature: formState.enableGraphicSignature,
    })
  }

  const needsStep2 =
    graphicSignatureEnabled &&
    formState.enableGraphicSignature &&
    formState.selectedFile?.type === 'application/pdf'

  const signatureTypes = SIGNATURE_TYPES.filter(type => !type.isFree).map(
    type => ({
      value: type.documentSignFormat,
      label: t(`documentAdd.signatureTypes.${type.value}`),
    })
  )

  const getAcceptFor = (format: DocumentSignatureFormat) => {
    const config = SIGNATURE_TYPES.find(s => s.documentSignFormat === format)

    if (!config || config.allowedFileTypes.length === 0) return undefined

    const parts = config.allowedFileTypes.flatMap(allowedType => {
      if (allowedType === '.pdf') return ['application/pdf', '.pdf']
      if (allowedType === '.xml') return ['application/xml', 'text/xml', '.xml']
      return [allowedType]
    })

    return Array.from(new Set(parts)).join(',')
  }

  const accept = getAcceptFor(formState.signatureFormat)

  const handleFileSelect = (file: File | null) => {
    setFormState(prev => ({ ...prev, selectedFile: file, error: null }))
  }

  const updateFormField = <K extends keyof FormState>(
    field: K,
    value: FormState[K]
  ) => {
    setFormState(prev => ({ ...prev, [field]: value }))
  }

  const displayError = formState.error ?? externalError

  const footer = (
    <div className='flex flex-col xs:flex-row gap-8 xs:gap-8'>
      <Button
        type='secondary'
        color='primary'
        size='md'
        onClick={onClose}
        disabled={isLoading}
        className='w-full xs:flex-1 xs:min-w-[140px]'
      >
        {t('common.action.cancel')}
      </Button>
      <Button
        onClick={handleContinue}
        disabled={!formState.selectedFile || isLoading}
        className='w-full xs:flex-1 xs:min-w-[140px]'
      >
        {needsStep2
          ? t('documentAdd.dialog.continue')
          : t('documentAdd.dialog.add')}
      </Button>
    </div>
  )

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('documentAdd.dialog.step1Title')}
      size='lg'
      footer={footer}
    >
      {isLoading && (
        <div className='absolute inset-0 z-50 bg-white/80 flex items-center justify-center'>
          <div className='w-16 h-16 border-4 border-cencert-border-gray border-t-cencert-blue rounded-full animate-spin'></div>
        </div>
      )}
      <div className='pt-10 flex flex-col h-full'>
        {/* Error display */}
        {displayError && (
          <Alert
            variant='error'
            message={displayError}
            className='mx-10 mb-8'
            onDismiss={() => {
              setFormState(prev => ({ ...prev, error: null }))
              onErrorDismiss?.()
            }}
          />
        )}

        {/* STEP 1: File & Type Selection */}
        {/* File upload area */}
        <FileSelector
          selectedFile={formState.selectedFile}
          onFileSelect={handleFileSelect}
          onError={error => updateFormField('error', error)}
          validateFile={validateFile}
          className='mx-10 mb-10'
          {...(accept && { accept })}
        />

        {graphicSignatureEnabled &&
          formState.selectedFile &&
          formState.selectedFile.type === 'application/pdf' &&
          (formState.signatureType === 'freeSignature' ||
            formState.signatureFormat === 'PAdES') && (
            <Checkbox
              label={t('documentAdd.graphicSignature.label')}
              infoText={t('documentAdd.graphicSignature.infoText')}
              checked={formState.enableGraphicSignature}
              onChange={(e: Event) => {
                const target = e.target as HTMLInputElement
                updateFormField('enableGraphicSignature', target.checked)
              }}
              className='mx-10 mb-6'
            />
          )}

        {/* Signature type selection */}
        <div className='bg-cencert-bg-gray flex justify-center p-10'>
          <div>
            <div>
              <RadioGroup
                name='signatureType'
                options={[
                  ...(isFreeSignatureEnabled
                    ? [
                        {
                          value: 'freeSignature',
                          label: t(
                            'documentAdd.signatureOptions.freeSignature'
                          ),
                        },
                      ]
                    : []),
                  {
                    value: 'paidSignature',
                    label: t('documentAdd.signatureOptions.paidSignature'),
                    infoText: t(
                      'documentAdd.signatureOptions.paidSignatureInfo'
                    ),
                  },
                ]}
                value={formState.signatureType}
                onChange={value => updateFormField('signatureType', value)}
              />
            </div>

            {/* Signature code input - only show for paid signature type */}
            {formState.signatureType === 'paidSignature' && (
              <div className='pl-7 pt-5 rounded-3xl space-y-6 w-[300px]'>
                <Input
                  label={t('documentAdd.signatureOptions.signatureCodeLabel')}
                  value={formState.signatureCode}
                  required
                  onChange={(e: Event) => {
                    const target = e.target as HTMLInputElement
                    updateFormField('signatureCode', target.value)
                    onInputCodeChange?.()
                  }}
                  maxLength={36}
                  className='border-cencert-border-gray focus:ring-cencert-blue'
                  error={inputCodeError ?? ''}
                />
                <Select
                  label={t('documentAdd.signatureOptions.signatureFormatLabel')}
                  options={signatureTypes}
                  value={formState.signatureFormat}
                  onChange={(e: Event) => {
                    const target = e.target as HTMLSelectElement
                    updateFormField(
                      'signatureFormat',
                      target.value as DocumentSignatureFormat
                    )
                  }}
                  infoText={t('documentAdd.signatureFormats.info')}
                  className='border-cencert-border-gray focus:ring-cencert-blue'
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  )
}
